import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';
import { executionModeSchema } from './workspace';
import { reviewModeSchema } from './workflow-type';

/** Parsed reference to a mergeable artifact (e.g. GitHub PR). */
export interface ReviewArtifactRef {
  kind: 'github_pr';
  owner: string;
  repo: string;
  pullNumber: number;
  url?: string;
}

/** Per-file diff evidence for review. */
export interface DiffEvidenceEntry {
  file: string;
  diff: string;
  changes: string;
}

/** Review artifact stored in runs.review_artifact — the structured hand-off for platform review tools. */
export interface ReviewArtifact {
  title: string;
  summary: string;
  referenceUrl?: string;
  referenceRef?: ReviewArtifactRef;
  diffEvidence?: { filesChanged: string[]; diffs: DiffEvidenceEntry[] };
  presentedByAgentId?: string;
  presentedAt?: string;
  reviewRounds?: number;
  escalatedForHumanReview?: boolean;
  reviewerApproved?: boolean;
}

export const reviewGateStatuses = [
  'pending',
  'approved',
  'changes_requested',
  'auto_approved',
] as const;
export const reviewGateStatusSchema = z.enum(reviewGateStatuses);
export type ReviewGateStatus = z.infer<typeof reviewGateStatusSchema>;

export const reviewGateSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    workspaceId: idSchema.nullish(),
    runId: idSchema,
    agentId: idSchema,
    status: reviewGateStatusSchema.default('pending'),
    executionMode: executionModeSchema.default('assist'),
    /** Who satisfies this gate (Epic L): none = informational, ai = independent/self review, human. */
    reviewerKind: reviewModeSchema.default('human'),
    /** Role label or agent id of the reviewer (e.g. an AI reviewer agent id). */
    reviewerRef: z.string().nullish(),
    recommendedAction: z.string().default(''),
    confidence: z.number().min(0).max(1).default(0),
    contributors: z.array(z.string()).default([]),
    evidence: z.record(z.unknown()).default({}),
    output: z.string().nullish(),
    decidedBy: idSchema.nullish(),
    reason: z.string().nullish(),
    publishedAt: z.coerce.date().nullish(),
  })
  .merge(timestampsSchema);
export type ReviewGate = z.infer<typeof reviewGateSchema>;

export const reviewDecisionSchema = z.object({
  approve: z.boolean(),
  reason: z.string().max(2000).optional(),
});
export type ReviewDecision = z.infer<typeof reviewDecisionSchema>;

/** Evidence shape used to decide whether a human/AI review wait is warranted. */
export interface ReviewEvidenceSummary {
  diffEvidence?: { filesChanged?: string[] } | null;
  referenceUrl?: string;
  referenceRef?: { kind?: string } | null;
}

/**
 * True when the run produced something worth blocking on (a parsed GitHub PR
 * ref or a non-empty git diff). A bare repository URL is not enough — agents
 * often attach one when blocked on clone/auth, which must not open a human
 * outcome gate. Conversational replies and clone-only runs stay informational.
 */
export function hasMaterialReviewEvidence(evidence: ReviewEvidenceSummary): boolean {
  if (evidence.referenceRef?.kind === 'github_pr') return true;
  if ((evidence.diffEvidence?.filesChanged?.length ?? 0) > 0) return true;
  return false;
}

const BLOCKED_OUTCOME_RE =
  /\b(blocked|cannot access|can't access|unable to (access|clone|read)|clone auth|authentication failed|reconnect\/refresh|github integration|credentials? (are |is )?(failing|invalid|expired|missing)|token .+ expired)\b|\[auth\]/i;

/**
 * True when the agent finished Execute without deliverable work and reported a
 * blocker (auth/clone/integration). Approving such an outcome should retry
 * rather than publish; rejecting should spawn remediation with feedback.
 */
export function isBlockedReviewOutcome(input: {
  output?: string | null;
  evidence?: ReviewEvidenceSummary | null;
}): boolean {
  if (hasMaterialReviewEvidence(input.evidence ?? {})) return false;
  const text = (input.output ?? '').trim();
  if (!text) return false;
  return BLOCKED_OUTCOME_RE.test(text);
}

/** Review gate enriched with run context for the Review & Governance UI. */
export interface EnrichedReviewGate extends ReviewGate {
  run: {
    /** The original user request / input that triggered this run. */
    input: string;
    /** Current status of the associated run. */
    status: string;
    /** When the run was created. */
    createdAt: string;
    /** Chat session (conversation) the run belongs to, when started from Collaborate. */
    conversationId?: string;
  };
}

/** Verdict produced by the independent AI / self reviewer (Epic L1). */
export interface AiReviewVerdict {
  approve: boolean;
  /** Reviewer's confidence in its own judgment [0,1]; low values fall through to a human. */
  confidence: number;
  rationale: string;
}

/** A3 — per-workspace policy resolving execution mode + gate behavior. */
export const reviewPolicySchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    workspaceId: idSchema,
    /** Taxonomy label key this policy applies to; null = workspace default. */
    labelKey: z.string().nullish(),
    /** Agent this policy applies to; null = any agent. */
    agentId: idSchema.nullish(),
    executionMode: executionModeSchema.default('assist'),
    /** How the review gate is satisfied (Epic L); aligns with workflow types. */
    reviewMode: reviewModeSchema.default('human'),
    /** In `assist` mode, outcomes below this confidence require a human gate. */
    confidenceThreshold: z.number().min(0).max(1).default(0.7),
    requiredReviewers: z.array(z.string()).default([]),
  })
  .merge(timestampsSchema);
export type ReviewPolicy = z.infer<typeof reviewPolicySchema>;

export const createReviewPolicySchema = reviewPolicySchema.pick({
  labelKey: true,
  agentId: true,
  executionMode: true,
  reviewMode: true,
  confidenceThreshold: true,
  requiredReviewers: true,
});
export type CreateReviewPolicy = z.infer<typeof createReviewPolicySchema>;

export const updateReviewPolicySchema = createReviewPolicySchema.partial();
export type UpdateReviewPolicy = z.infer<typeof updateReviewPolicySchema>;

/** Assembled, audit-ready run evidence bundle (Epics I + P). */
export interface RunEvidence {
  run: {
    id: string;
    agentId: string;
    status: string;
    executionMode?: string | null;
    currentStage?: string | null;
    confidenceLane?: string | null;
    labelKey?: string | null;
    workflowTypeKey?: string | null;
    resumedFromRunId?: string | null;
    input: string;
    output?: string | null;
    createdAt?: string | Date;
  };
  /** Resume chain (ancestors → self → descendants) for the timeline (Epic N2). */
  lineage: unknown[];
  stages: unknown[];
  steps: unknown[];
  delegations: unknown[];
  reviewGates: ReviewGate[];
  clarifications: unknown[];
  learning: unknown[];
  publishDeliveries: unknown[];
  /** Files generated during the run (PDF reports, uploads). */
  outputFiles?: unknown[];
  audit: unknown[];
  /** Quick tallies for the timeline header / export summary. */
  counts: {
    stages: number;
    steps: number;
    delegations: number;
    reviewGates: number;
    clarifications: number;
    learning: number;
    publishDeliveries: number;
    audit: number;
  };
  assembledAt: string;
}
